import { Link, useNavigate } from "react-router-dom";
import Header from "../../components/Header/Header";
import s from "./BrokersApplicationsList.module.scss";
import { Input, Table, Tabs, Tooltip } from "antd";
import type { ColumnsType } from "antd/es/table";
import {
  CreditCardOutlined,
  EditOutlined,
  FileDoneOutlined,
  MailOutlined,
} from "@ant-design/icons";
import type { TableProps } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../redux/store";
import {
  getBrokerOtherApplicationDetails,
  getBrokersApplicationDetails,
  getBrokersApplicationsList,
  getBrokersOthersApplicationsList,
  getCorrectedApplicationsList,
  getCountriesWithPosts,
  getManagerServicesByCountry,
  getMessagesList,
  getPaidApplicationsList,
  getPriorityApplicationsList,
  getRegisteredApplicationsList,
  searchApplications,
  setActiveTab,
  setCurrentOtherApplicationsPageNumber,
  setCurrentPageNumber,
  setLastSearchValue,
} from "../../redux/ApplicationSlice";
import { useEffect, useRef, useState } from "react";
import moment from "moment";
import Footer from "../../components/Footer/Footer";
import MobileHeader from "../../components/MobileHeader/MobileHeader";
import useWindowDimensions from "../../components/WindowDimensions/WindowDimensions";
import {
  getCountryStatusTag,
  getListStatusTag,
  getStatusTag,
} from "../../utils/functions";
import MobileApplicationsList from "../MobileApplicationsList/MobileApplicationsList";
import { ICreateApplicationResponse } from "../../types";
import Loading from "../../components/Loading/Loading";
import { getExchangeRate } from "../../redux/BillingSlice";
import { useTranslation } from "react-i18next";

import { getStatus, TDropdown } from "../../data";
import { Root } from "react-dom/client";
import TabPane from "antd/es/tabs/TabPane";
import type { GetProps } from "antd";
interface TableType {
  number: number;
  status: any;
  application_number: string;
  registration_date: string;
  user?: string;
}
type Props = {};
const { Search } = Input;
type SearchProps = GetProps<typeof Input.Search>;

const BrokersApplicationsList = (props: Props) => {
  const dispatch: AppDispatch = useDispatch();
  const navigate = useNavigate();

  const { width } = useWindowDimensions();
  const activeTab = useSelector(
    (state: RootState) => state.application.activeTab,
  );
  const applicationsList = useSelector(
    (state: RootState) => state.application.applicationsList,
  );

  const user = useSelector((state: RootState) => state.user.user);

  const [data, setData] = useState<ICreateApplicationResponse[] | undefined>(
    applicationsList?.results,
  );
  const currentStatePage = useSelector(
    (state: RootState) => state.application.currentPage,
  );
  const currentOtherApplicationsPage = useSelector(
    (state: RootState) => state.application.currentOtherApplicationsPage,
  );
  const [currentPage, setCurrentPage] = useState<number>(currentStatePage || 1);
  const [otherApplicationsCurrentPage, setOtherApplicationsCurrentPage] =
    useState<number>(currentOtherApplicationsPage || 1);

  const lang = useSelector((state: RootState) => state.application.lang);
  const { t } = useTranslation();

  const [pagination, setPagination] = useState({
    current: applicationsList?.pagination?.current_page || 1,
    pageSize: applicationsList?.pagination?.page_size || 30,
    total: applicationsList?.pagination?.total_items || 100,
  });

  const [otherApplicationsPagination, setOtherApplicationsPagination] =
    useState({
      current: applicationsList?.pagination?.current_page || 1,
      pageSize: applicationsList?.pagination?.page_size || 30,
      total: applicationsList?.pagination?.total_items || 100,
    });

  const currentPageRef = useRef(currentPage);
  const otherApplicationsCurrentPageRef = useRef(otherApplicationsCurrentPage);

  useEffect(() => {
    currentPageRef.current = currentPage;
    otherApplicationsCurrentPageRef.current = otherApplicationsCurrentPage;
  }, [currentPage]);

  useEffect(() => {
    const fetchData = async ({ page }: { page: number }) => {
      let res;

      if (activeTab === "myApplications") {
        res = await dispatch(getBrokersApplicationsList(page));
        if (
          res.payload &&
          res.type === "applications/getBrokersApplicationsList/fulfilled" &&
          "results" in res.payload
        ) {
          setData(res.payload.results);
          setPagination({
            current: res.payload.pagination?.current_page,
            pageSize: res.payload.pagination?.page_size,
            total: res.payload.pagination?.total_items,
          });
        }
      }

      if (activeTab === "otherApplications") {
        res = await dispatch(getBrokersOthersApplicationsList(page));
        if (
          res.payload &&
          res.type ===
            "applications/getBrokersOthersApplicationsList/fulfilled" &&
          "results" in res.payload
        ) {
          setData(res.payload.results);
          setOtherApplicationsPagination({
            current: res.payload.pagination?.current_page,
            pageSize: res.payload.pagination?.page_size,
            total: res.payload.pagination?.total_items,
          });
        }
      }
    };

    fetchData({
      page:
        activeTab === "myApplications"
          ? currentPageRef.current
          : otherApplicationsCurrentPageRef.current,
    });

    const interval = setInterval(() => {
      const page =
        activeTab === "myApplications"
          ? currentPageRef.current
          : otherApplicationsCurrentPageRef.current;

      fetchData({ page });
    }, 100000);

    return () => clearInterval(interval);
  }, [dispatch]);

  const getNextPage = async (page: number) => {
    if (activeTab === "myApplications") {
      setCurrentPage(page);
      dispatch(setCurrentPageNumber(page));
      currentPageRef.current = page;

      if (page === undefined || page === null) return;

      try {
        const res = await dispatch(getBrokersApplicationsList(page));
        if (!res.payload || !("results" in res.payload)) {
          console.error("Unexpected API response:", res);
          return;
        }

        if (res.type === "applications/getBrokersApplicationsList/fulfilled") {
          if ("results" in res.payload) {
            setData(res.payload.results);
            setPagination({
              current: res?.payload?.pagination?.current_page,
              pageSize: res?.payload?.pagination.page_size,
              total: res.payload.pagination.total_items,
            });
          } else {
            console.error("Unexpected API response:", res.payload);
          }
        }
      } catch (error) {
        console.error("Error in getNextPage:", error);
      }
    }

    if (activeTab === "otherApplications") {
      setOtherApplicationsCurrentPage(page);
      dispatch(setCurrentOtherApplicationsPageNumber(page));
      otherApplicationsCurrentPageRef.current = page;

      if (page === undefined || page === null) return;
      try {
        const res = await dispatch(getBrokersOthersApplicationsList(page));
        if (!res.payload || !("results" in res.payload)) {
          console.error("Unexpected API response:", res);
          return;
        }

        if (
          res.type === "applications/getBrokersOthersApplicationsList/fulfilled"
        ) {
          if ("results" in res.payload) {
            setData(res.payload.results);
            setOtherApplicationsPagination({
              current: res?.payload?.pagination?.current_page,
              pageSize: res?.payload?.pagination.page_size,
              total: res.payload.pagination.total_items,
            });
          } else {
            console.error("Unexpected API response:", res.payload);
          }
        }
      } catch (error) {
        console.error("Error in getNextPage:", error);
      }
    }
  };

  const columns: ColumnsType<TableType> = [
    {
      title: "№",
      dataIndex: "number",
      width: 20,
      align: "center",
    },
    {
      title: t("Статус"),
      dataIndex: `status`,
      width: 300,
    },
    {
      title: t("Номер заявки"),
      dataIndex: `application_number`,
      width: 200,
      align: "center",
      render: (text) => <div className={s.font}>{text}</div>,
    },
    {
      title: t("№ транспортного средства"),
      dataIndex: `vehicle_number`,
      width: 200,
      align: "center",
      render: (text) => <div className={s.font}>{text}</div>,
    },
    {
      title: t("Дата регистрации"),
      dataIndex: `registration_date`,
      width: 200,
      align: "center",
      render: (text) => <div className={s.font}>{text}</div>,
    },
    {
      title: t("Заявление"),
      dataIndex: `application`,
      width: 50,
    },
  ];

  const getPaymentAmount = async (id: number) => {
    const res = await dispatch(getBrokersApplicationDetails(id));
    if (res.type === "applications/getApplicationDetails/fulfilled") {
      navigate(`/payment/${id}`);
    }
  };

  const getMessages = async (id: number) => {
    const res = await dispatch(getMessagesList(Number(id)));

    if (res.type === "messages/getMessagesList/fulfilled") {
      navigate(`/messages/${id}`);
    }
  };
  const onSearch: SearchProps["onSearch"] = async (value, _e, info) => {
    const res = await dispatch(
      searchApplications({ search_term: value, page: 1 }),
    );

    dispatch(setLastSearchValue(value));
    console.log("res", res);
    if (res.type === "applications/searchApplications/fulfilled") {
      navigate("/search-results");
    }
  };

  const onTabChange = async (tab: string) => {
    dispatch(setActiveTab(tab));

    if (tab === "myApplications") {
      const res = await dispatch(getBrokersApplicationsList(1));
      if (
        res.payload &&
        "results" in res.payload &&
        res.type === "applications/getBrokersApplicationsList/fulfilled"
      ) {
        setData(res.payload.results);
        setPagination({
          current: res.payload.pagination?.current_page,
          pageSize: res.payload.pagination?.page_size,
          total: res.payload.pagination?.total_items,
        });
      }
    }

    if (tab === "otherApplications") {
      const res = await dispatch(getBrokersOthersApplicationsList(1));

      if (
        res.payload &&
        "results" in res.payload &&
        res.type === "applications/getBrokersOthersApplicationsList/fulfilled"
      ) {
        setData(res.payload.results);
        setOtherApplicationsPagination({
          current: res.payload.pagination?.current_page,
          pageSize: res.payload.pagination?.page_size,
          total: res.payload.pagination?.total_items,
        });
      }
    }
  };

  const [prevPage, setPrevPage] = useState<number>(1);
  const [prevEmployeeId, setPrevEmployeeId] = useState<number | undefined>(
    undefined,
  );

  return (
    <div>
      {width > 985 ? <Header /> : <MobileHeader />}
      <div className={s.header}>
        <div className={s.top_header}>
          <div className={s.header_left}>
            <div className={s.user_btn}>
              <Link
                to="/createApplication"
                className={s.btn}
                onClick={() => {
                  dispatch(getCountriesWithPosts());
                  dispatch(getManagerServicesByCountry());
                }}
              >
                {t("Зарегистрировать новую заявку")}
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className={s.content}>
        <Tabs
          defaultActiveKey="myApplications"
          type="card"
          activeKey={activeTab}
          onChange={(key) => onTabChange(key)}
        >
          <TabPane tab={t("Поступившие заказы")} key="otherApplications">
            <div className={s.form}>
              {width > 700 ? (
                <Table<TableType>
                  bordered
                  pagination={otherApplicationsPagination}
                  columns={columns}
                  className="table"
                  rowClassName={(_, index) =>
                    index % 2 === 0 ? "table-row-light" : "table-row-dark"
                  }
                  onChange={(pagination) => {
                    if (pagination?.current) {
                      getNextPage(pagination.current);
                    }
                  }}
                  dataSource={
                    data &&
                    data.map((it, index) => ({
                      key: it.id,
                      number: index + 1,

                      status: (
                        <div className={s.status_bar}>
                          {it.routes?.map((route) => (
                            <div key={route.id} className={s.status_route_item}>
                              <div className={s.status_bar_country}>
                                {route.transit_country_name}
                              </div>

                              <div className={s.status_services_list}>
                                {route.services?.map((srv) => (
                                  <div
                                    key={srv.id}
                                    className={s.status_service_row}
                                  >
                                    <div className={s.status_bar_service}>
                                      {srv.service_name} ({srv.broker_name})
                                    </div>
                                    <div className={s.status_bar_status}>
                                      {getListStatusTag(srv.status, "sm", t)}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      ),
                      application_number: it?.application_number,
                      vehicle_number: it?.vehicle_number,
                      registration_date: moment(it?.created_at).format(
                        "DD.MM.YYYY - HH:mm",
                      ),
                      // declarant: it?.assigned_declarant?.full_name,
                      // name: it?.user?.full_name,
                      payment:
                        it?.status === "new" ||
                        it?.status === "incomplete" ||
                        it?.status === "corrected" ? (
                          <div> {"-"} </div>
                        ) : (
                          <div
                            onClick={() => {
                              getPaymentAmount(it.id);
                              dispatch(getExchangeRate());
                            }}
                          >
                            <Tooltip title={t("Просмотреть оплату")} key={2}>
                              <CreditCardOutlined style={{ color: "gray" }} />
                            </Tooltip>
                          </div>
                        ),
                      application: (
                        <Link
                          to={`/broker/application/${it?.id}`}
                          className={s.edit}
                          onClick={() => {
                            dispatch(getBrokerOtherApplicationDetails(it.id));
                          }}
                        >
                          <Tooltip title={t("Просмотр/Редактирование")} key={1}>
                            <EditOutlined />
                          </Tooltip>
                        </Link>
                      ),
                      history: (
                        <div
                          onClick={() => getMessages(it.id)}
                          className={s.edit}
                        >
                          <Tooltip title={t("Просмотреть историю")} key={2}>
                            <MailOutlined />
                            {/* {it?.unread_message_count_declarant > 0 && (
                              <div className={s.messages_count}>
                                {it.unread_message_count_declarant}
                              </div>
                            )} */}
                          </Tooltip>
                        </div>
                      ),
                      declaration: (
                        <div className={s.edit}>
                          {/* {it.declaration ? (
                            <Link to={it.declaration} className={s.edit}>
                              <Tooltip title={t("Декларация")} key={4}>
                                <FileDoneOutlined />
                              </Tooltip>
                            </Link>
                          ) : (
                            "-"
                          )} */}
                        </div>
                      ),
                    }))
                  }
                />
              ) : (
                <MobileApplicationsList />
              )}
            </div>
          </TabPane>
          <TabPane tab={t("Мои заказы")} key="myApplications">
            <div className={s.form}>
              {width > 700 ? (
                <Table<TableType>
                  bordered
                  pagination={pagination}
                  columns={columns}
                  className="table"
                  rowClassName={(_, index) =>
                    index % 2 === 0 ? "table-row-light" : "table-row-dark"
                  }
                  onChange={(pagination) => {
                    if (pagination?.current) {
                      getNextPage(pagination.current);
                    }
                  }}
                  dataSource={
                    Array.isArray(data)
                      ? data?.map((it, index) => ({
                          key: it?.id,
                          number: index + 1,

                          status: (
                            <div className={s.status_bar}>
                              {it.routes?.map((route) => (
                                <div
                                  key={route.id}
                                  className={s.status_route_item}
                                >
                                  <div className={s.status_bar_country}>
                                    {route.transit_country_name}
                                  </div>

                                  <div className={s.status_services_list}>
                                    {route.services?.map((srv) => (
                                      <div
                                        key={srv.id}
                                        className={s.status_service_row}
                                      >
                                        <div className={s.status_bar_service}>
                                          {srv.service_name}&nbsp;
                                          <strong>({srv.broker_name})</strong>
                                        </div>
                                        <div className={s.status_bar_status}>
                                          {getListStatusTag(
                                            srv.status,
                                            "sm",
                                            t,
                                          )}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              ))}
                            </div>
                          ),

                          application_number: it?.application_number,
                          vehicle_number: it?.vehicle_number,
                          registration_date: moment(it?.created_at).format(
                            "DD.MM.YYYY - HH:mm",
                          ),
                          // declarant: it?.assigned_declarant?.full_name,
                          // name: it?.user?.full_name,
                          payment:
                            it?.status === "new" ||
                            it?.status === "incomplete" ||
                            it?.status === "corrected" ? (
                              <div> {"-"} </div>
                            ) : (
                              <div
                                onClick={() => {
                                  getPaymentAmount(it.id);
                                  dispatch(getExchangeRate());
                                }}
                              >
                                <Tooltip
                                  title={t("Просмотреть оплату")}
                                  key={2}
                                >
                                  <CreditCardOutlined
                                    style={{ color: "gray" }}
                                  />
                                </Tooltip>
                              </div>
                            ),
                          application: (
                            <Link
                              to={`/broker/application/${it?.id}`}
                              className={s.edit}
                              onClick={() => {
                                dispatch(getBrokersApplicationDetails(it.id));
                              }}
                            >
                              <Tooltip
                                title={t("Просмотр/Редактирование")}
                                key={1}
                              >
                                <EditOutlined />
                              </Tooltip>
                            </Link>
                          ),
                          history: (
                            <div
                              onClick={() => getMessages(it.id)}
                              className={s.edit}
                            >
                              <Tooltip title={t("Просмотреть историю")} key={2}>
                                <MailOutlined />
                                {/* {it?.unread_message_count_declarant > 0 && (
                                <div className={s.messages_count}>
                                  {it.unread_message_count_declarant}
                                </div>
                              )} */}
                              </Tooltip>
                            </div>
                          ),
                          declaration: (
                            <div className={s.edit}>
                              {/* {it.declaration ? (
                              <Link to={it.declaration} className={s.edit}>
                                <Tooltip title={t("Декларация")} key={4}>
                                  <FileDoneOutlined />
                                </Tooltip>
                              </Link>
                            ) : (
                              "-"
                            )} */}
                            </div>
                          ),
                        }))
                      : []
                  }
                />
              ) : (
                <MobileApplicationsList />
              )}
            </div>
          </TabPane>
        </Tabs>
      </div>
      <Footer />
    </div>
  );
};

export default BrokersApplicationsList;
